import type { LensColumnKind } from '~/types/leLens';

import { PRE_AVERAGED_NAME, RATIO_NAME, UNIT_SUFFIX } from './columnClassification';
import type { FieldNode } from './fieldExpression';

/**
 * Why a rate cannot be combined, and which two totals to use instead.
 *
 * A rate carries its own denominator, so a rate is only correct when it is
 * computed from the totals of its parts: sum(numerator) / sum(denominator).
 * Adding rates gives a number that is not a rate at all, and averaging them
 * weights a day with ten orders the same as a day with ten thousand. Neither
 * fault is visible in the result, which is why this refuses rather than warns.
 */
export type RatioFault = {
  /** The column that cannot be combined this way. */
  column: string;
  /** Plain business English, naming the two totals to use instead. */
  message: string;
  /** The replacement, when this dataset carries both parts. */
  parts?: { of: string; per: string };
};

/** A column already expressed per unit of something else. */
export function isPerUnitColumn(column: string): boolean {
  const name = column.trim();
  return Boolean(name) && (RATIO_NAME.test(name) || PRE_AVERAGED_NAME.test(name));
}

/**
 * The numerator and denominator an LE rate is built from, keyed by the word the
 * rate is named after. Listed rather than inferred: `margin_pct` divides by TTV
 * and `mroas` divides by spend, and no rule over the column name alone can tell
 * which denominator a rate meant.
 */
const RATIO_PARTS: Array<{ names: RegExp; of: Array<RegExp>; per: Array<RegExp> }> = [
  { names: /\b(mroas|roas)\b/i, of: [/margin/i, /revenue/i, /ttv/i], per: [/spend/i] },
  { names: /\b(ctr|click.?through)\b/i, of: [/click/i], per: [/impression/i] },
  {
    names: /\b(conversion|cvr)\b/i,
    of: [/order/i, /booking/i, /purchase/i],
    per: [/session/i, /visit/i, /click/i, /impression/i],
  },
  {
    names: /\b(renewal|retention|churn)\b/i,
    of: [/renew/i, /retain/i, /churn/i],
    per: [/eligible/i, /due/i, /member/i, /subscriber/i],
  },
  {
    names: /\b(aov|arpu|arpo|per_order|per_booking|average order value|order value)\b/i,
    of: [/ttv/i, /revenue/i, /sales/i, /value/i, /fee/i],
    per: [/order/i, /booking/i, /customer/i, /member/i],
  },
  {
    names: /\b(attach|penetration|adoption)\b/i,
    of: [/attach/i, /adopt/i, /with_/i],
    per: [/order/i, /booking/i, /customer/i],
  },
  { names: /\b(discount|voucher)\b/i, of: [/discount/i, /voucher/i], per: [/ttv/i, /revenue/i, /sales/i] },
  {
    names: /\boccupancy\b/i,
    of: [/booked/i, /sold/i, /occupied/i],
    per: [/available/i, /allocated/i, /capacity/i, /room/i],
  },
  { names: /\brefund/i, of: [/refund/i], per: [/ttv/i, /revenue/i, /sales/i, /order/i, /booking/i] },
  { names: /\b(margin|gm)\b/i, of: [/margin/i], per: [/ttv/i, /revenue/i, /sales/i] },
  { names: /\b(savings?)\b/i, of: [/saving/i], per: [/ttv/i, /revenue/i, /fee/i] },
];

/** The first column any of these patterns finds, in the order given. */
function firstMatch(
  patterns: ReadonlyArray<RegExp>,
  columns: ReadonlyArray<string>,
  exclude?: string,
): string | undefined {
  for (const pattern of patterns) {
    const found = columns.find((column) => column !== exclude && pattern.test(column));
    if (found) {
      return found;
    }
  }
  return undefined;
}

/** Whether a column's declared kind rules it out as a ratio numerator or denominator candidate,
 *  whatever its name says: an id or a label is never what a rate is made of, and neither is the
 *  date it is grouped by. A column absent from `kinds`, or a dataset with no `kinds` at all,
 *  is never excluded here: the name-based matching below is the only signal for those. */
function excludedByDeclaredKind(column: string, kinds: Readonly<Record<string, LensColumnKind>> | undefined): boolean {
  const declared = kinds?.[column];
  return declared === 'dimension' || declared === 'date';
}

/**
 * Chooses the best of a ratio's numerator or denominator candidates: the first one of `patterns`
 * matches, exactly as before declared kinds existed (this is still what tells a real refund
 * amount apart from a coincidentally-matching measure when several are declared). Only when no
 * pattern matches at all does a column `kinds` declares a measure step in, since a declared fact
 * naming an unusually-spelled measure beats matching nothing. A dataset that has not declared any
 * of this (`kinds` undefined) never reaches that fallback, which is what keeps every caller that
 * never had columns to declare working unchanged.
 */
function pickRatioCandidate(
  candidates: ReadonlyArray<string>,
  patterns: ReadonlyArray<RegExp>,
  kinds: Readonly<Record<string, LensColumnKind>> | undefined,
  exclude?: string,
): string | undefined {
  const eligible = candidates.filter((entry) => entry !== exclude && !excludedByDeclaredKind(entry, kinds));
  return firstMatch(patterns, eligible) ?? (kinds && eligible.find((entry) => kinds[entry] === 'measure'));
}

/**
 * The two totals the rate a request names is made of, resolved from the wording
 * rather than from a column name.
 *
 * "Refund rate" and "average refund value" both name refunds and divide by
 * different things, so the request itself has to pick the denominator.
 *
 * `kinds`, when given, is the same declared-column-kind map `TierDataset.kinds` carries: a column
 * declared a dimension or a date is dropped from candidacy before either pattern is tried, and a
 * column declared a measure is preferred over one a pattern merely happens to match.
 */
export function ratioPartsForAsk(
  ask: string,
  columns: ReadonlyArray<string>,
  kinds?: Readonly<Record<string, LensColumnKind>>,
): { of: string; per: string } | undefined {
  const usable = columns.filter((column) => !isPerUnitColumn(column));
  const rule = RATIO_PARTS.find((entry) => entry.names.test(ask));
  if (!rule) {
    return undefined;
  }
  const of = pickRatioCandidate(usable, rule.of, kinds);
  const per = of ? pickRatioCandidate(usable, rule.per, kinds, of) : undefined;
  return of && per ? { of, per } : undefined;
}

/** Denominators a share is taken over when the rate's own name does not say. */
const FALLBACK_DENOMINATORS = /(ttv|revenue|sales|orders|bookings|sessions|impressions|spend|cost|nights|members)/i;

/** The word a rate is named after, with the rate word and any unit stripped. */
function ratioStem(column: string): string {
  return column
    .replace(UNIT_SUFFIX, '')
    .replace(RATIO_NAME, '_')
    .replace(PRE_AVERAGED_NAME, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * The two columns a rate should be computed from, when this result carries
 * them. Returns nothing rather than a guess: naming the wrong denominator in a
 * refusal sends the reader to a number that is differently wrong.
 *
 * `kinds`, when given, is threaded into both the by-name lookup and the stem fallback below: see
 * `ratioPartsForAsk`'s own doc for what it changes.
 */
export function ratioParts(
  column: string,
  columns: ReadonlyArray<string>,
  kinds?: Readonly<Record<string, LensColumnKind>>,
): { of: string; per: string } | undefined {
  const candidates = columns.filter((entry) => entry !== column && !isPerUnitColumn(entry));
  if (!candidates.length) {
    return undefined;
  }

  const byName = ratioPartsForAsk(column, candidates, kinds);
  if (byName) {
    return byName;
  }

  // A share of something names its own numerator: `hotel_share` over
  // `hotel_ttv_aud`, divided by whichever total the result carries. The stem match stays a plain
  // substring test (it is not one of the name patterns pickRatioCandidate walks), but a column
  // declared a dimension or a date is excluded from it exactly as from the denominator search.
  const stem = ratioStem(column);
  const of = stem
    ? candidates.find(
        (entry) => !excludedByDeclaredKind(entry, kinds) && entry.toLowerCase().includes(stem.toLowerCase()),
      )
    : undefined;
  const per = pickRatioCandidate(candidates, [FALLBACK_DENOMINATORS], kinds, of);
  return of && per ? { of, per } : undefined;
}

/**
 * Whether combining this column this way produces a number to refuse, and what
 * to do instead.
 *
 * `sum` and `avg` are both refused over a per-unit column: the sum is not a
 * rate and the average is an unweighted one, and a reader cannot tell either
 * from the correct figure by looking at it.
 */
export function ratioOfSumsFault(
  column: string,
  aggregate: string,
  columns: ReadonlyArray<string> = [],
  kinds?: Readonly<Record<string, LensColumnKind>>,
): RatioFault | undefined {
  const name = column.trim();
  if (!name || !isPerUnitColumn(name) || !['sum', 'avg', 'mean', 'average'].includes(aggregate)) {
    return undefined;
  }

  const parts = ratioParts(name, columns, kinds);
  const instead = parts
    ? `Measure it as ${parts.of} divided by ${parts.per}, worked out from each group's totals.`
    : 'Measure it as its numerator divided by its denominator, worked out from each group’s totals, and name both columns.';

  const wrong =
    aggregate === 'sum'
      ? `Adding rows of ${name} together does not give a rate: two days at 30% do not make 60%.`
      : `Averaging ${name} counts every row equally, so a day with ten orders moves it as much as a day with ten thousand.`;

  return { column: name, message: `${wrong} ${instead}`, ...(parts ? { parts } : {}) };
}

/**
 * Whether a calculated field would produce an average of rates.
 *
 * Read from the parsed formula rather than its text, because the position a
 * rate column sits in decides whether the formula is wrong. `margin_pct *
 * orders` is a reader weighting a rate back into its numerator, which is
 * correct; `margin_pct + 0` and `round(margin_pct, 2)` carry the rate straight
 * through to whatever the card does with it, which is not.
 */
export function ratioExpressionFault(node: FieldNode, columns: ReadonlyArray<string> = []): RatioFault | undefined {
  const offending = carriedThrough(node);
  if (!offending) {
    return undefined;
  }

  const parts = ratioParts(offending, columns);
  const instead = parts
    ? `Write it as ${parts.of} / ${parts.per} on totals instead.`
    : 'Write it over the two totals the rate is made of instead, naming both columns.';
  return {
    column: offending,
    message: `${offending} is already a rate, so a field that carries it through cannot be rolled up: adding it is not a rate and averaging it weights every row equally. ${instead}`,
    ...(parts ? { parts } : {}),
  };
}

/**
 * The first per-unit column the formula passes through unchanged.
 *
 * Multiplication, division and comparison are all ways of using a rate that
 * survive being rolled up, so they are walked into rather than reported.
 */
function carriedThrough(node: FieldNode): string | undefined {
  if (node.kind === 'column') {
    return isPerUnitColumn(node.name) ? node.name : undefined;
  }
  if (node.kind === 'unary') {
    return node.operator === '-' ? carriedThrough(node.operand) : undefined;
  }
  if (node.kind === 'binary') {
    if (node.operator !== '+' && node.operator !== '-') {
      return undefined;
    }
    return carriedThrough(node.left) ?? carriedThrough(node.right);
  }
  if (node.kind === 'call') {
    if (node.name === 'if') {
      // The test is a comparison; the two results are what the field returns.
      return carriedThrough(node.args[1]) ?? carriedThrough(node.args[2]);
    }
    return node.args.map(carriedThrough).find(Boolean);
  }
  return undefined;
}

/**
 * Why a calculated field cannot be rolled up, if it cannot.
 *
 * Two faults, both invisible in the result. A rate written on each row is
 * averaged the moment a card groups it, and a field that carries an existing
 * rate column through has the same problem one step removed.
 */
export function fieldRollupFault(
  field: { name: string; expression: string; when?: 'row' | 'aggregate' },
  node: FieldNode,
  columns: ReadonlyArray<string> = [],
): RatioFault | undefined {
  if (field.when !== 'aggregate' && expressionIsRate(field.expression, field.name)) {
    const parts = ratioParts(field.name, columns) ?? ratioPartsForAsk(field.name, columns);
    const instead = parts
      ? `Declare it on totals, as ${parts.of} / ${parts.per}.`
      : 'Declare it on totals, so the formula divides each group’s totals rather than each row.';
    return {
      column: field.name,
      message: `${field.name} is a rate, and a rate worked out on each row is averaged as soon as a card groups it: a $100 booking then counts as much as a $30,000 one. ${instead}`,
      ...(parts ? { parts } : {}),
    };
  }
  return ratioExpressionFault(node, columns);
}

/**
 * Whether a field expression is a rate, judged from the formula rather than the
 * name. A single division of two columns is a rate whatever it is called, which
 * is what decides that it has to be evaluated on totals.
 */
export function expressionIsRate(expression: string, name = ''): boolean {
  if (isPerUnitColumn(name)) {
    return true;
  }
  const body = expression.trim();
  if (!body.includes('/')) {
    return false;
  }
  // A division by a literal is a unit conversion, not a rate: `nights / 7`.
  const divisor = body.slice(body.lastIndexOf('/') + 1).trim();
  return !/^[\d\s().]+$/.test(divisor);
}
